import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import React from "react";
import { Button, Dropdown, MenuProps } from "antd";
import Icon, { SettingOutlined } from "@ant-design/icons";
import { Link, useParams } from "react-router";

import { SparklesIcon } from "./icons";
import { Heading3, NoWrap } from "../../../styles/typography";
import { OrganizationSwitcher } from "./organization-select";
import EnitBrandIcon from "../../../styles/logo/enit-brand-signal.svg";
import { AuthService } from "../../auth";
import { FeatureFlagService } from "../../feature-flag-service";
import { useFeature } from "../../contexts/feature-flags";
import { useCopilot } from "../../contexts/copilot";

function CopilotTriggerButton() {
  const enabled = useFeature("license/ai-assistant");

  if (!enabled) {
    return null;
  }

  return <CopilotTrigger />;
}

function CopilotTrigger() {
  const { toggleCopilot } = useCopilot();

  return (
    <Button
      type="text"
      title={t`AI assistant`}
      aria-label={t`AI assistant`}
      onClick={toggleCopilot}
      icon={
        <div>
          <SparklesIcon className="h-4 w-4 svg-magenta" />
        </div>
      }
    />
  );
}

function UserMenu() {
  const orgaId = useParams().orgaId ?? "app";

  const menuOnClick: MenuProps["onClick"] = (e) => {
    if (e.key === "logout") {
      FeatureFlagService.reset();
      AuthService.signout();
    }
  };

  const menu: MenuProps = {
    items: [
      {
        key: "profile",
        label: (
          <Link to="/user/profile" target="_blank">
            <Trans>Profile</Trans>
          </Link>
        ),
      },
      {
        key: "organization",
        label: (
          <Link to={`/${orgaId}/settings`} target="_blank">
            <Trans>Settings</Trans>
          </Link>
        ),
      },
      { type: "divider" as const },
      {
        key: "imprint",
        label: (
          <Link to="https://enit.io/de/impressum" target="_blank">
            <Trans>Imprint</Trans>
          </Link>
        ),
      },
      {
        key: "privacy",
        label: (
          <Link to="https://enit.io/de/datenschutz" target="_blank">
            <Trans>Privacy</Trans>
          </Link>
        ),
      },
      { type: "divider" as const },
      {
        key: "support",
        label: (
          <a
            href="mailto:service@enit.io?subject=Support-Anfrage Enit hub"
            rel="noreferrer"
            target="_blank"
          >
            <Trans>Contact support</Trans>
          </a>
        ),
      },
      { key: "logout", label: t`Logout` },
    ],
    onClick: menuOnClick,
  };

  return (
    <Dropdown menu={menu} trigger={["click"]} destroyOnHidden>
      <SettingOutlined />
    </Dropdown>
  );
}

export function Titlebar({ title }: { title: string }) {
  title = title.trim();
  const pageTitle = !title ? "Enit hub" : `${title} - Enit hub`;

  return (
    <>
      <title>{pageTitle}</title>
      <div className="flex flex-row justify-end items-center bg-white h-[var(--titlebar-height)] px-6 gap-6 border-b-2 border-gray-300 print:hidden">
        <div className="absolute left-1/2 -translate-x-1/2">
          <Heading3 className="max-w-[40vw] truncate" italic>
            <NoWrap>{title}</NoWrap>
          </Heading3>
        </div>
        <OrganizationSwitcher className="max-w-[15vw]" />
        <CopilotTriggerButton />
        <UserMenu />
      </div>
    </>
  );
}

export function Topbar({ children }: React.PropsWithChildren) {
  return (
    <>
      <title>Enit hub</title>
      <div className="flex flex-row items-center justify-between bg-[var(--topbar-color)] text-white h-[var(--titlebar-height)] z-10 sticky top-0 px-6 gap-6 print:hidden ">
        <Link to="/app/home">
          <span className="inline-block space-x-2 font-bold">
            <Icon component={EnitBrandIcon} />
            <span>Enit hub</span>
          </span>
        </Link>
        <div className="flex flex-row items-center gap-6">
          {children}
          <UserMenu />
        </div>
      </div>
    </>
  );
}
